import { HiveAccent } from '@/components/brand/hive-accent';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

import { api } from '@/lib/api';
import { planAlert } from '@/lib/plan-alert';
import { cn } from '@/lib/utils';

/**
 * Workspace-wide plan notice.
 *
 * Deliberately informational: an expired trial or lapsed subscription does NOT
 * switch anything off (see entitlements.checkAccess - only a manual suspend
 * does), so this must read as a heads-up, never as a lockout. It says plainly
 * that everything still works, so nobody panics that their leads have stopped.
 *
 * Dismissable per browser per state, so a tenant who has seen it can keep
 * working; a change of state (trial ending -> expired) shows it again.
 */
export function PlanBanner() {
  const { data } = useQuery({
    queryKey: ['billing', 'plan-banner'],
    // The plan alone (GET /api/billing/plan): the full billing read counts the
    // month's visits and was the slowest call on every page.
    queryFn: async () => (await api.get('/api/billing/plan')).data,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const plan = data?.plan;
  const alert = planAlert(plan ? {
    sub_status: plan.status,
    trial_end: plan.trial_end,
    comped_until: plan.comped_until,
    current_period_end: plan.current_period_end,
  } : null);

  const key = alert ? `mm_plan_notice_${alert.code}_${alert.days ?? ''}` : null;
  const [dismissed, setDismissed] = React.useState(false);
  React.useEffect(() => {
    setDismissed(key ? localStorage.getItem(key) === '1' : false);
  }, [key]);

  if (!alert || alert.code === 'suspended' || dismissed) return null;

  return (
    <div className={cn(
      'flex items-center gap-3 border-b px-4 py-1.5 text-[13px]',
      alert.level === 'danger'
        ? 'card-critical text-red-800 dark:text-red-200'
        : 'card-attention text-amber-900 dark:text-amber-200',
    )}>
      <HiveAccent className="-my-2 w-16" />
      <span className="min-w-0 flex-1">
        <span className="font-semibold">{alert.title}.</span>{' '}
        Everything is still working - nothing has been switched off.{' '}
        <Link to="/settings?tab=billing" className="font-medium underline underline-offset-2">
          View billing
        </Link>
      </span>
      <button
        onClick={() => { if (key) localStorage.setItem(key, '1'); setDismissed(true); }}
        aria-label="Dismiss"
        className="shrink-0 rounded p-0.5 opacity-60 transition hover:opacity-100"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
